/**
 * Home Layout Manager Types
 *
 * TypeScript types for the unified Home Layout Manager admin page.
 */

// =============================================================================
// Banner Types
// =============================================================================

export type BannerLinkType = 'category' | 'product' | 'brand' | 'brands-page' | 'store' | 'custom';

export interface BannerItemDraft {
  id: string;
  name: string;
  image: string;
  imageUrl: string;
  redirectUrl: string | null;
  /** Type of link target from LinkTargetSelector */
  linkType: BannerLinkType | null;
  /** UUID of the linked entity (category/product/brand/store) */
  linkedEntityId: string | null;
  position: number;
  isActive: boolean;
  isDefault: boolean;
}

export interface BannerPositionDraft {
  name: 'top banner' | 'slider banner' | 'bottom banner';
  label: string;
  description: string;
  maxItems: number;
  displayOrder: number;
  type: 'static' | 'slider';
  bannerId: string | null;
  items: BannerItemDraft[];
  collapsed?: boolean;
}

export interface BannerConfig {
  positions: BannerPositionDraft[];
}

/** Data passed from BannerItemFormDialog when saving in draft mode */
export interface BannerItemDraftSaveData {
  id: string;
  name: string;
  image: string;
  imageUrl: string;
  redirectUrl: string | null;
  linkType: BannerLinkType | null;
  linkedEntityId: string | null;
  isActive: boolean;
  isDefault: boolean;
}

// =============================================================================
// Category Types
// =============================================================================

export type HomeFilterType = 'category' | 'tag' | 'brand' | 'newOrPopular' | 'combined' | 'none';

export interface CategoryDraft {
  id: string;
  name: string;
  imageUrl: string | null;
  /** Stored icon filename; Publish Live writes it to the category. */
  image?: string | null;
  position: number;
  /** Position in home page horizontal scrollers (independent ordering) */
  scrollerPosition: number;
  /** Position in category carousel icons (independent ordering) */
  carouselPosition: number;
  /** Position in shop page grid icons (independent ordering) */
  shopPosition: number;
  isActive: boolean;
  showOnHomePage: boolean;
  showInCarousel: boolean;
  showInCarouselWeb: boolean;
  showInShop: boolean;
  showTagFilters: boolean;
  respectsEarlyCutoff: boolean;
  homeFilterType: HomeFilterType;
  homeFilterValue: string | null;
  legacyExternalId: string | null;
  /** Whether this category has products at the selected store (preview only) */
  hasProductsAtStore?: boolean;
  /**
   * Locked global position across all screens (Home, Carousel, Shop).
   * When set, this category will always appear at this position on any tab where it's visible.
   * Only one category can be locked at a given position.
   * null = not locked (uses per-tab positions)
   */
  lockedGlobalPosition: number | null;
}

export interface NewPopularConfig {
  homeFilterType: HomeFilterType;
  homeFilterValue: string | null;
  name: string;
}

export interface CategoryConfig {
  categories: CategoryDraft[];
  newPopularConfig?: NewPopularConfig;
}

// =============================================================================
// Layout Config Types (New Independent Ordering System)
// =============================================================================

/**
 * Carousel vertical position configuration
 * Controls where the category carousel appears among the horizontal scrollers
 */
export interface CarouselVerticalConfig {
  /** 'auto' = 50% split, 'custom' = use customPosition value */
  mode: 'auto' | 'custom';
  /** 1-based position among scrollers (only used when mode is 'custom') */
  customPosition: number | null;
}

/**
 * Featured brands configuration
 * Controls which brands appear first in the brands section
 */
export interface FeaturedBrandsConfig {
  /** Brand IDs for mobile view (max 4 shown prominently) */
  mobileBrandIds: string[];
  /** Brand IDs for web view (max 8 shown prominently) */
  webBrandIds: string[];
  /** Maximum visible featured brands on mobile */
  mobileMaxVisible: number;
  /** Maximum visible featured brands on web */
  webMaxVisible: number;
}

/**
 * Brand data for the brands selector
 */
export interface Brand {
  id: string;
  name: string;
  /**
   * DEAR-era artefact (the "Brandxx - 001" placeholder family SKU). Brands added
   * from Content Control don't get one, so this is nullable.
   */
  sku: string | null;
  imageUrl: string | null;
  position: number;
}

/**
 * Brand position in draft
 */
export interface BrandPosition {
  id: string;
  position: number;
  name: string;
}

/**
 * Brand positions configuration
 */
export interface BrandPositionsConfig {
  positions: BrandPosition[];
  scrollerConfig?: {
    mode: 'auto' | 'custom';
    customPosition: number | null;
  };
}

// =============================================================================
// Draft Types
// =============================================================================

export interface UserInfo {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
}

export interface DraftState {
  bannerConfig: BannerConfig;
  categoryConfig: CategoryConfig;
  /** Brand positions for home page brand scroller */
  brandPositions: BrandPositionsConfig;
  /** Carousel vertical position configuration */
  carouselVerticalConfig: CarouselVerticalConfig;
  /** Featured brands configuration */
  featuredBrandsConfig: FeaturedBrandsConfig;
}

export interface GetDraftResponse {
  draft: DraftState;
  lastModifiedBy: UserInfo | null;
  lastModifiedAt: string;
  hasChanges: boolean;
  changeCounts?: {
    banners: number;
    categories: number;
    brands: number;
  };
}

// =============================================================================
// Publish Types
// =============================================================================

export interface PublishResponse {
  publishId: string | null;
  publishedAt: string;
  changesSummary: {
    bannersModified: number;
    categoriesModified: number;
    brandsModified: number;
  };
}

export interface PublishHistoryItem {
  id: string;
  publishedAt: string;
  publishNotes: string | null;
  publishedByUser: UserInfo | null;
}

export interface PublishHistoryResponse {
  data: PublishHistoryItem[];
  total: number;
  page: number;
  hasMore: boolean;
}

// =============================================================================
// Preview Types
// =============================================================================

export type ViewMode = 'mobile' | 'web';

export interface StoreInfo {
  id: string;
  name: string;
}

export interface PreviewResponse {
  banners: BannerPositionDraft[];
  categories: CategoryDraft[];
  storeInfo: StoreInfo | null;
}

// =============================================================================
// Presence Types (Collaboration)
// =============================================================================

export type ViewingSection = 'banners' | 'categories' | 'preview' | null;

export interface ActivePresence {
  user: UserInfo | null;
  viewingSection: ViewingSection;
  editingItemId: string | null;
  lastPingAt: string;
}

export interface PresenceResponse {
  activeUsers: ActivePresence[];
}

// =============================================================================
// Suggestion Types
// =============================================================================

export type SuggestionType = 'warning' | 'tip' | 'info';

export interface Suggestion {
  type: SuggestionType;
  title: string;
  description: string;
  actionLabel: string;
  actionParams: Record<string, unknown>;
}

export interface SuggestionsResponse {
  suggestions: Suggestion[];
}

// =============================================================================
// Change Detection Types
// =============================================================================

export type ChangeType = 'added' | 'removed' | 'modified';
export type ChangeCategory = 'position' | 'property' | 'both';

export interface PropertyChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface ChangeItem {
  id: string;
  name: string;
  type: ChangeType;
  /** Categorizes whether the change is position-only, property-only, or both */
  changeCategory?: ChangeCategory;
  /** Position change details for descriptive display */
  positionChange?: {
    from: number;
    to: number;
    field: string;
  };
  /** Field-level property changes for descriptive display */
  propertyChanges?: PropertyChange[];
}

export interface ChangesResponse {
  banners: ChangeItem[];
  categories: ChangeItem[];
  brands: ChangeItem[];
  totalChanges: number;
}

// =============================================================================
// Operations Tracking Types
// =============================================================================

/** Tracks an intentional drag-and-drop operation for accurate change counting */
export interface DragOperation {
  /** Unique ID of the moved item */
  itemId: string;
  /** Human-readable name for display */
  itemName: string;
  /** Section where the move occurred */
  section: 'home' | 'carousel' | 'shop' | 'brands' | 'banners';
  /** Position field that was changed (e.g., scrollerPosition, carouselPosition) */
  positionField: string;
  /** Original position before any moves (from live state) */
  fromPosition: number;
  /** Current position after all moves */
  toPosition: number;
  /** Timestamp of last move */
  lastMovedAt: string;
}

// =============================================================================
// Context Types
// =============================================================================

export interface HomeLayoutContextValue {
  // State
  draft: DraftState | null;
  liveState: DraftState | null;
  isLoading: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  error: string | null;
  lastModifiedBy: UserInfo | null;
  lastModifiedAt: string | null;
  hasChanges: boolean;
  changes: ChangesResponse | null;

  // Preview state
  selectedStoreId: string | null;
  viewMode: ViewMode;
  compareMode: boolean;

  // Presence state
  activeUsers: ActivePresence[];

  // Suggestions
  suggestions: Suggestion[];

  // Operations tracking (for meaningful change counting)
  pendingOperations: DragOperation[];
  meaningfulChangeCount: number;

  // Actions
  updateDraft: (updates: Partial<DraftState>) => Promise<void>;
  updateBannerConfig: (config: Partial<BannerConfig>) => Promise<void>;
  updateCategoryConfig: (config: CategoryConfig) => Promise<void>;
  updateCarouselVerticalConfig: (config: CarouselVerticalConfig) => Promise<void>;
  updateFeaturedBrandsConfig: (config: FeaturedBrandsConfig) => Promise<void>;
  updateBrandPositions: (config: BrandPositionsConfig) => Promise<void>;
  resetDraft: () => Promise<void>;
  publish: (notes?: string) => Promise<PublishResponse>;
  rollback: (publishId: string) => Promise<void>;
  refreshDraft: () => Promise<void>;

  // Operations actions
  trackDragOperation: (op: DragOperation) => void;
  clearOperations: () => void;

  // Preview actions
  setSelectedStoreId: (storeId: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setCompareMode: (enabled: boolean) => void;

  // Presence actions
  updatePresence: (section: ViewingSection, editingItemId?: string | null) => void;

  // Publish notification (from another admin)
  publishNotification: { publishedByName: string } | null;
  dismissPublishNotification: () => void;
}

// =============================================================================
// Store Types (for dropdown)
// =============================================================================

export interface Store {
  id: string;
  name: string;
  isActive: boolean;
  externalId?: string;
}

// =============================================================================
// UI State Types
// =============================================================================

export interface CollapsedSections {
  banners: boolean;
  categories: boolean;
}

export interface BannerSectionState {
  expandedPositions: Set<string>;
}

// =============================================================================
// Store scope
// =============================================================================

/**
 * Sentinel `selectedStoreId` meaning "every store, combined" rather than one outlet.
 *
 * Content Control edits a single global draft, so this — not a specific store — is
 * the default scope: with a store selected, anything that outlet doesn't stock
 * renders as "No products at this store", which hid catalogue-wide content behind
 * one shop's shelf. Picking a real store in "Preview As" is still how you check
 * what a given outlet will actually show.
 */
export const ALL_LOCATIONS = '__all_locations__';
